import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "../components/argus/Logo";
import CaptchaWidget from "../components/argus/CaptchaWidget";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Signup() {
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [aup, setAup] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [err, setErr] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!aup) {
      setErr("Please accept the Acceptable Use Policy to continue.");
      return;
    }
    try {
      await signup({ ...form, accept_aup: true, captcha_token: captchaToken });
      toast.success("Welcome to ArgusSecure.");
      navigate("/app/welcome");
    } catch (ex) {
      // Surface the real backend reason. FastAPI returns `detail` as a string
      // for HTTPException (rate-limit, dup email) and as an array for Pydantic
      // 422 errors — handle both, and fall back to status-code-specific copy
      // before the generic message.
      const d = ex?.response?.data?.detail;
      let msg;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = `${d[0].msg}${d[0].loc ? ` (${d[0].loc.slice(-1)})` : ""}`;
      else if (ex?.response?.status === 429) msg = "Too many signup attempts from this network. Wait 60 seconds and try again.";
      else if (ex?.response?.status === 0 || !ex?.response) msg = "Can't reach the ArgusSecure API. Check your connection and try again.";
      else msg = "Couldn't create your account. Try again.";
      setErr(msg);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-block mb-8">
          <Logo size={28} />
        </Link>
        <h1 className="font-display text-4xl mb-2" data-testid="signup-title">Start scanning in 90 seconds.</h1>
        <p className="text-ink/60 mb-8">Free forever for one project. No card.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="signup-form" noValidate aria-describedby={err ? "signup-error" : undefined}>
          <div>
            <Label htmlFor="name">Your name</Label>
            <Input id="name" required autoComplete="name" value={form.name} onChange={onChange("name")} className="h-12 mt-1" data-testid="signup-name" aria-required="true" />
          </div>
          <div>
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={onChange("email")} className="h-12 mt-1" data-testid="signup-email" aria-required="true" aria-invalid={err && /email/i.test(err) ? "true" : undefined} />
          </div>
          <div>
            <Label htmlFor="password">Password (min 8 chars)</Label>
            <Input id="password" type="password" minLength={8} required autoComplete="new-password" value={form.password} onChange={onChange("password")} className="h-12 mt-1" data-testid="signup-password" aria-required="true" aria-invalid={err && /password/i.test(err) ? "true" : undefined} aria-describedby="signup-password-hint" />
            <span id="signup-password-hint" className="sr-only">Minimum eight characters.</span>
          </div>
          <div>
            <Label htmlFor="org">Team name (optional)</Label>
            <Input id="org" autoComplete="organization" value={form.org_name} onChange={onChange("org_name")} className="h-12 mt-1" data-testid="signup-org" placeholder="e.g. your team or company" />
          </div>

          <label className="flex items-start gap-3 text-sm text-ink/70 cursor-pointer pt-2" data-testid="signup-aup-row">
            <Checkbox checked={aup} onCheckedChange={(v) => setAup(!!v)} data-testid="signup-aup" aria-label="I agree to the Acceptable Use Policy" />
            <span>
              I will only scan systems I own or have written permission to test. I've read the{" "}
              <Link to="/legal/acceptable-use" className="text-gold underline">Acceptable Use Policy</Link>.
            </span>
          </label>

          <CaptchaWidget onToken={setCaptchaToken} />

          {err && (
            <div
              id="signup-error"
              role="alert"
              aria-live="assertive"
              className="text-sm text-sev-critical bg-sev-critical/5 border border-sev-critical/20 rounded-md p-3"
              data-testid="signup-error"
            >
              {err}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full h-12 bg-gold text-paper hover:bg-gold2 font-semibold" data-testid="signup-submit" aria-busy={loading ? "true" : "false"}>
            {loading ? "Creating your account…" : "Create My Account"} <ArrowRight size={16} className="ml-2" aria-hidden="true" />
          </Button>
        </form>

        <p className="text-sm text-ink/60 mt-6">
          Already on ArgusSecure? <Link to="/login" className="text-gold hover:underline" data-testid="signup-login-link">Sign in →</Link>
        </p>
      </div>
    </div>
  );
}
